import { useState, useEffect, useCallback } from 'react';
import { API_PATH, request } from '../config/api';

const API_BASE = API_PATH;

/**
 * useActivityContent
 *
 * Loads an activity's saved content from the server on mount.
 * Provides a `saveContent(data)` function that PATCHes the content blob.
 *
 * @param {string|number} eventId
 * @param {string|number} activityId
 * @returns {{ content, isLoading, isSaving, saveStatus, saveContent }}
 *   saveStatus: 'idle' | 'saving' | 'saved' | 'error'
 */
const useActivityContent = (eventId, activityId) => {
    const [content, setContent] = useState(null);   // null = not yet loaded
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle'|'saving'|'saved'|'error'

    // Load on mount
    useEffect(() => {
        if (!eventId || !activityId) { setIsLoading(false); return; }

        let cancelled = false;
        setIsLoading(true);

        request(`${API_BASE}/events/${eventId}/activities/${activityId}`)
            .then(r => r.json())
            .then(data => {
                if (!cancelled) {
                    setContent(data.content || {});
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setContent({});
                    setIsLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [eventId, activityId]);

    // Save handler
    const saveContent = useCallback(async (data) => {
        if (!eventId || !activityId) return;
        setIsSaving(true);
        setSaveStatus('saving');

        try {
            const res = await request(
                `${API_BASE}/events/${eventId}/activities/${activityId}`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ content: data }),
                }
            );

            if (!res.ok) throw new Error('Save failed');

            const updated = await res.json();
            setContent(updated.content || data);
            setSaveStatus('saved');

            // Reset to idle after 2.5s
            setTimeout(() => setSaveStatus('idle'), 2500);
        } catch {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } finally {
            setIsSaving(false);
        }
    }, [eventId, activityId]);

    return { content, isLoading, isSaving, saveStatus, saveContent };
};

export default useActivityContent;
